import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { env } from '@/config/env';
import { cn } from '@/lib/utils';
import { fromApiTime, useMoveBooking, useMoveTargets } from './events.api';

/**
 * Move a booking into a different time slot.
 *
 * Past slots are hidden until asked for. The original noted that asking for
 * past dates is the same act as intending to move into one, so there is no
 * second confirmation once the toggle is on.
 */
export function MoveBookingDialog({
  booking,
  open,
  onOpenChange,
  onMoved,
}: {
  /** Only the id is used; any record carrying one will do. */
  booking: { id: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMoved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'es' ? 'es-PR' : 'en-US';

  const [includePast, setIncludePast] = useState(false);
  const [addSpot, setAddSpot] = useState(true);
  const [reason, setReason] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const targets = useMoveTargets(open ? (booking?.id ?? null) : null, includePast);
  const move = useMoveBooking();

  if (!booking) return null;

  const when = (value: string | null) => {
    const date = fromApiTime(value, env.timezoneOffset);
    if (!date) return '—';
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      // `fromApiTime` returns the venue clock in UTC fields; pin the zone so
      // the browser's offset is not applied a second time.
      timeZone: 'UTC',
    }).format(date);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('events.moveBooking')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('events.moveBookingBody')}
            {targets.data?.eventName ? ` · ${targets.data.eventName}` : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <Label className="text-xs font-semibold">
              {t('events.includePast')}
            </Label>
            <Switch checked={includePast} onCheckedChange={setIncludePast} />
          </div>

          {targets.isPending && (
            <div className="space-y-1">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          )}

          {targets.data?.timeslots.length === 0 && (
            <p className="text-muted-foreground py-4 text-center text-xs">
              {t('events.noMoveTargets')}
            </p>
          )}

          {/* The spinner sits outside the scroll box so it can stick. */}
          <div className="relative rounded-lg">
            <BusyOverlay show={targets.isFetching && !targets.isPending} />
            <div
              className={cn(
                'max-h-56 space-y-1 overflow-y-auto',
                targets.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {(targets.data?.timeslots ?? []).map((target) => {
                const isFull = (target.spotsLeft ?? 0) <= 0;
                /*
                 * The API marks a full slot ineligible, but "add a spot if the
                 * slot is full" is precisely the answer to that — with it on, a
                 * full slot becomes a legitimate target and the move request
                 * carries `increaseTargetCapacity` to make room. Any other reason
                 * the API gives still blocks it.
                 */
                const selectable = target.eligible || (addSpot && isFull);
                return (
                <button
                  key={target.id}
                  type="button"
                  disabled={!selectable}
                  onClick={() => setSelectedId(target.id)}
                  className={cn(
                    'w-full rounded-lg border p-2 text-left text-sm transition-colors',
                    selectable
                      ? 'hover:bg-accent'
                      : 'cursor-not-allowed opacity-50',
                    selectedId === target.id && 'border-primary bg-accent'
                  )}
                >
                  <p className="font-medium">
                    {when(target.startTime)}
                    {target.isPast && (
                      <span className="text-muted-foreground ml-1.5 text-xs font-normal">
                        ({t('events.past')})
                      </span>
                    )}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {target.hostName && `${target.hostName} · `}
                    {target.booked ?? 0}/{target.maxCapacity ?? 0}
                    {isFull && ` · ${t('events.full')}`}
                  </p>
                  {/* Full but usable: say what will happen rather than leaving
                      the reason reading as a blocker. */}
                  {isFull && selectable && !target.eligible && (
                    <p className="text-warning text-xs">
                      {t('events.willAddSpot', {
                        capacity: (target.maxCapacity ?? 0) + 1,
                      })}
                    </p>
                  )}
                  {!selectable && target.ineligibleReason && (
                    <p className="text-destructive text-xs">
                      {target.ineligibleReason}
                    </p>
                  )}
                </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <Label className="text-xs font-semibold">
              {t('events.increaseCapacity')}
            </Label>
            <Switch
              checked={addSpot}
              onCheckedChange={(next) => {
                setAddSpot(next);
                // Switching it off can make the current choice invalid.
                if (!next) {
                  const chosen = targets.data?.timeslots.find(
                    (slot) => slot.id === selectedId
                  );
                  if (chosen && !chosen.eligible) setSelectedId(null);
                }
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.reason')}
            </Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={!selectedId || move.isPending}
            onClick={() =>
              move.mutate(
                {
                  bookingId: booking.id,
                  targetTimeSlotId: selectedId!,
                  reason,
                  increaseTargetCapacity: addSpot,
                  allowPastTimeSlot: includePast,
                },
                {
                  onSuccess: () => {
                    toast.success(t('events.bookingMoved'));
                    onOpenChange(false);
                    onMoved();
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('events.bookingActionFailed')
                    ),
                }
              )
            }
          >
            {move.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('events.move')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
